<template>
  <div class="AtlasLegend">
    <div class="AtlasLegend-item" v-for="item in items">
      <div
        class="AtlasLegend-swatch"
        :style="{ backgroundColor: item.color }"
      ></div>
      {{ item.text }}
    </div>
  </div>
</template>

<script>
import { orderBy, uniq } from 'lodash';

import { transforms } from '@datalas/charts';

export default {
  name: 'atlas-legend',

  props: {
    attributefield: {
      type: String,
    },

    colorBy: {
      type: [Number, String],
    },

    labelBy: {
      type: [Number, String],
    },

    transforms: {
      type: Array,
      default: () => [],
    },

    data: {
      type: Array,
      default: () => [],
    },

    sorts: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    sortedData() {
      if (!this.sorts.length) return this.data;

      if (typeof this.sorts === 'function') {
        return this.data.slice().sort(this.sorts);
      }

      const fields = this.sorts.map((s) => s.field);
      const orders = this.sorts.map((s) => s.order || 'asc');

      return orderBy(this.data, fields, orders);
    },

    attributeTransforms() {
      return this.transforms.filter(
        (t) => t.field === this.attributefield && t.attribute,
      );
    },

    colorTransform() {
      if (this.colorBy !== undefined) {
        const transform = this.transforms.find(({ id }) => id === this.colorBy);

        return transforms[transform.type](transform);
      }

      const transform = this.attributeTransforms.find(
        ({ attribute }) => attribute === 'colors',
      );

      return transforms[transform.type](transform);
    },

    colorDomain() {
      return this.colorTransform.options.type === 'scheme'
        ? uniq(this.sortedData.map((d) => d[this.colorTransform.field]))
        : undefined;
    },

    colorScale() {
      return this.colorTransform.transformer(this.colorDomain);
    },

    colorValues() {
      return uniq(this.sortedData.map((d) => d[this.colorTransform.field]));
    },

    labelTransform() {
      if (this.labelBy !== undefined) {
        const transform = this.transforms.find(({ id }) => id === this.labelBy);

        return transforms[transform.type](transform);
      }

      const transform = this.attributeTransforms.find(
        ({ attribute }) => attribute === 'labels',
      );

      return transform ? transforms[transform.type](transform) : null;
    },

    labelValues() {
      return this.labelTransform
        ? uniq(this.sortedData.map((d) => d[this.labelTransform.field]))
        : null;
    },

    labelDomain() {
      return this.labelTransform.options.type === 'scheme'
        ? this.labelValues
        : undefined;
    },

    labelScale() {
      return this.labelTransform.transformer(this.labelDomain);
    },

    items() {
      return this.labelValues
        ? this.labelValues.map((key, i) => ({
            color: this.colorScale(this.colorValues[i]),
            text: this.labelScale(key),
          }))
        : [];
    },
  },
};
</script>

<style lang="scss">
.AtlasLegend {
  $swatch-size: 15px;

  padding: 1px $gutter 15px;
  margin-top: -9px; // Offset first row of items margin-top
  font-size: 13px;
  line-height: $swatch-size;

  &-item {
    display: inline-block;
    margin-top: 9px; // For when items break to a second line
    margin-right: 20px;
    cursor: pointer;
    white-space: nowrap; // Download image appears to need this
  }

  &-swatch {
    @include size($swatch-size);
    position: relative;
    top: -1px;
    display: inline-block;
    margin-right: 5px;
    vertical-align: middle;
  }
}
</style>
